// The unwrap moment (A3): a stage that was wrapped for this child has opened. Once, on the next
// open: the wrapping paper tears down the middle and flies apart, the stage's name pops up with
// confetti, and the world behind shows the island (about 1.5 s; a tap skips it). Calm/Still:
// a fade. PLACEHOLDER look in 2D over the world stage; the wrapped island it opens is the 3D one
// built to the owner's pick (src/stage/trip/isles.ts). Sounds: unwrapWhoosh, then unwrapPop.
import { motion } from 'motion/react'
import { useEffect, useRef } from 'react'
import type { Stage } from '../../shared/pack/schema'
import { useModal } from '../app/Sheet'
import { buzz } from '../audio/haptics'
import { appSound } from '../audio/appSounds'
import { unwrapPop, unwrapWhoosh } from '../audio/synth'
import { springs } from '../motion/springs'
import { useMotionTier } from '../motion/tier'
import { fmt, t } from '../strings/en'

const CONFETTI = ['#FFC93C', '#FF4F4F', '#2F7BFF', '#36D399', '#fff', '#9B6BFF', '#FFC93C', '#FF4F4F', '#fff', '#36D399', '#2F7BFF', '#FFC93C']
/** How long the moment stays before handing back (ms), unless tapped. */
const STAY_MS = 2200

/** The torn inner edge of each half of the paper. */
const EDGE = [[18, 40], [4, 90], [22, 140], [6, 200], [24, 260], [8, 320], [26, 380], [6, 440], [22, 500], [4, 560], [20, 620], [6, 680], [24, 740], [8, 800], [20, 860], [0, 900]]
  .map(([x, y]) => `L${206 + x!},${y}`)
  .join(' ')

function Paper({ side, still }: { side: -1 | 1; still: boolean }) {
  // Each half: red wrapping paper with a gold ribbon band, its inner edge torn (the right half mirrored).
  return (
    <motion.svg
      className={`unwrap-paper ${side < 0 ? 'left' : 'right'}`}
      viewBox="0 0 230 900"
      preserveAspectRatio="none"
      aria-hidden
      initial={{ x: 0, rotate: 0, opacity: 1 }}
      animate={still ? { opacity: 0 } : { x: [0, side * 6, side * 480], rotate: [0, 0, side * 18], opacity: 1 }}
      transition={still ? { duration: 0.4 } : { duration: 0.75, times: [0, 0.25, 1], ease: 'easeIn' }}
    >
      <g transform={side < 0 ? undefined : 'translate(230 0) scale(-1 1)'}>
        <path d={`M0,0 H206 ${EDGE} H0 Z`} fill="#C8202A" />
        <rect x="150" y="0" width="34" height="900" fill="#F0B42A" />
        <path d={`M206,0 ${EDGE}`} fill="none" stroke="#1B2A3A" strokeWidth="4" />
        <g fill="#fff" opacity=".25">
          <circle cx="50" cy="120" r="10" />
          <circle cx="100" cy="300" r="8" />
          <circle cx="40" cy="520" r="12" />
          <circle cx="110" cy="700" r="9" />
        </g>
      </g>
    </motion.svg>
  )
}

export function Unwrap({ stage, onDone }: { stage: Stage; onDone(): void }) {
  const tier = useMotionTier()
  const still = tier === 'still'
  const calm = tier !== 'full'

  useEffect(() => {
    appSound('paper_tear', unwrapWhoosh)
    buzz('crack')
    const pop = setTimeout(() => appSound('gift_pop', unwrapPop), still ? 0 : 450)
    const end = setTimeout(onDone, STAY_MS)
    return () => {
      clearTimeout(pop)
      clearTimeout(end)
    }
  }, [onDone, still])

  const ref = useRef<HTMLDivElement>(null)
  useModal(ref, { onClose: onDone })

  const days = Math.round((Date.parse(stage.endDate) - Date.parse(stage.startDate)) / 86_400_000) + 1
  return (
    <div ref={ref} className="unwrap" role="dialog" aria-modal="true" tabIndex={-1} aria-label={t.unwrap.label(stage.name)} onClick={onDone}>
      <Paper side={-1} still={still} />
      <Paper side={1} still={still} />
      {!calm ? (
        <div className="confetti" aria-hidden>
          {CONFETTI.map((c, i) => (
            <i key={i} style={{ background: c, left: `${6 + i * 8}%`, animationDelay: `${0.35 + (i % 4) * 0.06}s` }} />
          ))}
        </div>
      ) : null}
      <motion.div
        className="unwrap-card panel"
        initial={still ? false : { scale: 0, y: 60 }}
        animate={{ scale: 1, y: 0 }}
        transition={{ delay: 0.4, ...springs.bounce }}
      >
        <span className="tag purple">{t.unwrap.tag}</span>
        <h2>{stage.name}!</h2>
        <p>{t.unwrap.sub(fmt.days(days))}</p>
      </motion.div>
      <p className="unwrap-skip">{t.unwrap.skip}</p>
    </div>
  )
}
